import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => { clearTimeout(t); window.removeEventListener('scroll', handleScroll); };
  }, []);

  return (
    <div ref={heroRef} className="relative w-full h-screen min-h-[700px] overflow-hidden">
      {/* Background Image with Parallax */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('https://readdy.ai/api/search-image?query=vibrant%20colorful%20Indian%20restaurant%20interior%20with%20bold%20hand%20painted%20mural%20wall%20featuring%20rich%20saffron%20orange%20turmeric%20yellow%20coral%20red%20truck%20art%20Bollywood%20folk%20painting%20style%20bright%20festive%20cultural%20art%20dining%20space%20dramatic%20warm%20lighting%20premium%20photography%20wide%20angle&width=1920&height=1080&seq=1101&orientation=landscape')",
          transform: `translateY(${scrollY * 0.3}px)`,
        }}
      ></div>

      {/* Multi-layer overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/55 to-black/35"></div>
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 60%, transparent 40%, rgba(0,0,0,0.4) 100%)' }}></div>

      {/* Mural-inspired top border */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-saffron via-turmeric via-coral to-teal"></div>

      {/* Decorative mandala pattern top-right */}
      <div className="absolute top-16 right-8 w-72 h-72 opacity-10 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,182,39,0.8) 1px, transparent 1px), radial-gradient(circle at 50% 50%, rgba(255,107,53,0.5) 30%, transparent 70%)',
          backgroundSize: '20px 20px, 100% 100%',
          borderRadius: '50%',
        }}
      ></div>

      {/* Decorative bottom-left pattern */}
      <div className="absolute bottom-12 left-8 w-48 h-48 opacity-8 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,107,53,0.6) 1px, transparent 1px)',
          backgroundSize: '14px 14px',
          borderRadius: '50%',
        }}
      ></div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${6 + i * 3}px`,
              height: `${6 + i * 3}px`,
              background: i % 3 === 0
                ? 'rgba(255,107,53,0.4)'
                : i % 3 === 1
                ? 'rgba(255,182,39,0.4)'
                : 'rgba(192,23,122,0.3)',
              top: `${10 + i * 10}%`,
              left: i % 2 === 0 ? `${4 + i * 5}%` : `${70 + i * 3}%`,
              animation: `float ${3 + i * 0.7}s ease-in-out infinite alternate`,
              animationDelay: `${i * 0.4}s`,
            }}
          ></div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-6 w-full">
        {/* Location tag */}
        <div
          className={`flex items-center gap-2 mb-6 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ transitionDelay: '0.1s' }}
        >
          <div className="h-px w-8 bg-saffron/60"></div>
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-saffron/90">Duncan, South Carolina</p>
          <div className="h-px w-8 bg-saffron/60"></div>
        </div>

        {/* Main Title */}
        <h1
          className={`font-serif font-bold leading-none text-white mb-3 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}
          style={{
            transitionDelay: '0.3s',
            letterSpacing: '-0.02em',
            fontSize: 'clamp(4rem, 14vw, 11rem)',
            textShadow: '0 0 80px rgba(255,107,53,0.4), 0 4px 40px rgba(0,0,0,0.5)',
          }}
        >
          CHASKA
        </h1>

        {/* Decorative divider */}
        <div
          className={`flex items-center gap-3 mb-4 transition-all duration-1000 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ transitionDelay: '0.5s' }}
        >
          <div className="h-px w-12 bg-gradient-to-r from-transparent to-saffron"></div>
          <div className="w-2 h-2 rounded-full bg-saffron"></div>
          <div className="w-1 h-1 rounded-full bg-turmeric"></div>
          <div className="w-2 h-2 rounded-full bg-coral"></div>
          <div className="h-px w-12 bg-gradient-to-l from-transparent to-coral"></div>
        </div>

        {/* Subtitle */}
        <p
          className={`font-accent text-2xl sm:text-3xl lg:text-4xl text-saffron mb-3 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '0.55s' }}
        >
          A craving worth coming back for
        </p>

        <p
          className={`font-sans text-sm sm:text-base text-white/70 mb-10 max-w-md transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ transitionDelay: '0.7s' }}
        >
          Authentic Indian flavors, lovingly crafted from generations of tradition
        </p>

        {/* CTA Buttons */}
        <div
          className={`flex flex-col sm:flex-row items-center gap-4 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ transitionDelay: '0.9s' }}
        >
          <Link
            to="/menu"
            className="bg-saffron text-white font-sans font-semibold text-sm px-9 py-4 rounded-full hover:bg-coral transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 hover:scale-105"
            style={{ boxShadow: '0 4px 20px rgba(255,107,53,0.4)' }}
          >
            <i className="ri-restaurant-2-line"></i>
            View Menu
          </Link>
          <a
            href="tel:+18645550123"
            className="bg-white/15 border border-white/40 backdrop-blur-sm text-white font-sans font-semibold text-sm px-9 py-4 rounded-full hover:bg-white/25 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 hover:scale-105"
          >
            <i className="ri-phone-line text-saffron"></i>
            Call to Reserve
          </a>
          <Link
            to="/gallery"
            className="text-white/80 font-sans text-sm px-6 py-4 rounded-full border border-white/20 hover:border-white/40 hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-image-line text-turmeric"></i>
            Gallery
          </Link>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="font-sans text-[10px] uppercase tracking-widest text-white/50">Scroll</span>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white/50"></i>
        </div>
      </div>

      <style>{`
        @keyframes float {
          from { transform: translateY(0px) rotate(0deg); }
          to { transform: translateY(-25px) rotate(15deg); }
        }
      `}</style>
    </div>
  );
}
